import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';

interface VideoPreviewModalProps {
  isOpen: boolean;
  title: string;
  videoUrl: string;
  posterUrl?: string;
  detailHref: string;
  detailLabel: string;
  onClose: () => void;
}

export const VideoPreviewModal = ({
  isOpen,
  title,
  videoUrl,
  posterUrl,
  detailHref,
  detailLabel,
  onClose,
}: VideoPreviewModalProps) => {
  const headingId = useId();
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !videoUrl) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-3 backdrop-blur-sm sm:p-6"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="relative w-full max-w-5xl overflow-hidden rounded-2xl border border-white/15 bg-slate-950 shadow-2xl shadow-black/50"
      >
        <button
          ref={closeButtonRef}
          type="button"
          className="absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white backdrop-blur transition hover:bg-black/80 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/30"
          onClick={onClose}
          aria-label="Close video preview"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="aspect-video w-full bg-black">
          <video
            key={videoUrl}
            src={videoUrl}
            poster={posterUrl}
            className="h-full w-full object-contain"
            controls
            autoPlay
            playsInline
            preload="metadata"
          />
        </div>

        <div className="flex flex-col gap-4 bg-white p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgb(var(--accent-solid))]">
              Video Preview
            </p>
            <h2 id={headingId} className="mt-1 truncate text-xl font-bold text-slate-950 sm:text-2xl">
              {title}
            </h2>
          </div>
          <Link
            to={detailHref}
            className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-lg bg-[rgb(var(--accent-solid))] px-5 py-2.5 text-sm font-semibold text-white transition hover:brightness-95"
            onClick={onClose}
          >
            {detailLabel}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>,
    document.body
  );
};
